import {useState} from 'react';

// This hook allows consumers of a component to optionally control its state by specifying
// or not specifying a value prop (or similar). A controlled component is a component that
// has its state managed by the consumer, rather than managing its own state. It is common
// to use controlled components e.g. when using a global state object like React Context
// or redux. Sometimes though it's convenient to allow a component to manage its own
// state, e.g. when demonstrating a component in Storybook.
//
// The return values can be used the same as `useState`, and it behaves identically to
// `useState` if the first parameter is undefined (i.e. the value prop wasn't provided).
//
// Example:
//
//     export const CustomInput({ value, onChange }) => {
//       const [value, setValue] = useOptionallyControlledState(value, "initial value", onChange);
//       return <input value={value} />;
//     }
//
//     /* Results in a controlled component */
//     <CustomInput value={mystate.foo} onChange={v => ...} />
//
//     /* Results in an uncontrolled component */
//     <CustomInput onChange={v => ...} />
//
//     /* Results in an uncontrolled component with an initial value */
//     <CustomInput initialValue="hello" onChange={v => ...} />
//
export const useOptionallyControlledState = (controlledValue, initialValue, onChange) => {
    const isControlled = controlledValue !== undefined;
    const [stateValue, setStateValue] = useState(initialValue);
    const value = isControlled ? controlledValue : stateValue;

    function setter(v) {
        if (!isControlled) setStateValue(v);
        if (onChange) onChange(v);
    }

    return [value, setter];
};
